import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Loader2, AlertTriangle, ArrowRight, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import DocumentProductView from '../components/DocumentProductView';
import VisualProductView from '../components/VisualProductView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SharedProduct = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const res = await axios.get(`${API}/share/${shareId}`);
        setData(res.data);
      } catch (err) {
        const status = err.response?.status;
        if (status === 410) setError('deactivated');
        else if (status === 404) setError('not_found');
        else setError('unknown');
      }
      setLoading(false);
    };
    fetchShared();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
          <p className="font-sans text-sm text-stone-500">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center" data-testid="share-error-page">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-6">
            {error === 'deactivated'
              ? <Lock className="w-8 h-8 text-stone-400" />
              : <AlertTriangle className="w-8 h-8 text-stone-400" />}
          </div>
          <h2 className="text-2xl font-serif font-bold text-stone-800 mb-2">
            {error === 'deactivated' ? 'The link is deactivated' : error === 'not_found' ? 'Product not found' : 'Something went wrong'}
          </h2>
          <p className="font-sans text-stone-500 mb-8">
            {error === 'deactivated'
              ? 'The owner has deactivated this share link.'
              : error === 'not_found'
              ? 'This share link does not exist or has expired.'
              : 'Could not load the product. Please try again later.'}
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full px-6 font-sans font-semibold"
          >
            Utforsk Create by Lumera <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  const { product, creator, allow_download } = data;
  const content = product.content;
  const isVisual = !!content?.slides;

  // No-op handlers for read-only mode
  const noop = () => {};

  return (
    <div className="min-h-screen bg-[#F7F5F2] dark:bg-background" data-testid="shared-product-page">
      {/* Minimal nav */}
      <nav className="bg-white dark:bg-card border-b border-[#d8cfc8]/50 dark:border-border/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-14">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/lumera-logo.png" alt="Lumera" className="w-8 h-8 rounded-full" />
            <span className="text-lg font-serif font-semibold text-[#111111]">Lumera</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Creator info */}
            <div className="flex items-center gap-2">
              {creator?.avatar_url ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${creator.avatar_url}`}
                  alt=""
                  className="w-7 h-7 rounded-full object-cover border border-stone-200"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : null}
              <span className="font-sans text-xs text-stone-500">
                Delt av <span className="font-semibold text-stone-700">{creator?.name || 'Lumera-bruker'}</span>
              </span>
            </div>
            <Button
              data-testid="share-cta-btn"
              onClick={() => navigate('/register')}
              className="bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full px-5 h-8 text-xs font-sans font-semibold"
            >
              Create your own
            </Button>
          </div>
        </div>
      </nav>

      {/* Product display */}
      <div className="max-w-7xl mx-auto">
        {!allow_download && (
          <div className="text-center py-3 bg-stone-50 border-b border-stone-100">
            <p className="font-sans text-xs text-stone-400">
              Read-only preview — download is disabled
            </p>
          </div>
        )}

        {isVisual ? (
          <VisualProductView
            content={content}
            onSave={noop}
            onNew={noop}
            saving={false}
            onUpdateContent={noop}
            onEditSettings={noop}
            projectId={null}
            readOnly={!allow_download}
          />
        ) : (
          <DocumentProductView
            content={content}
            productType={product.product_type}
            onSave={noop}
            onNew={noop}
            saving={false}
            onUpdateContent={noop}
            onEditSettings={noop}
            projectId={null}
            readOnly={!allow_download}
          />
        )}
      </div>

      {/* Lumera branding footer */}
      <div className="bg-white border-t border-[#d8cfc8]/50 py-8 text-center" data-testid="share-branding">
        <div className="flex items-center justify-center gap-2.5 mb-3">
          <img src="/lumera-logo.png" alt="Lumera" className="w-7 h-7 rounded-full" />
          <span className="font-serif font-semibold text-[#111111]">Made with Create by Lumera</span>
        </div>
        <p className="font-sans text-sm text-[#6F6964] mb-4">
          Create your own digitale products med AI-powered design.
        </p>
        <Button
          data-testid="share-footer-cta"
          onClick={() => navigate('/')}
          variant="outline"
          className="rounded-full px-6 font-sans font-semibold border-[#d8cfc8] hover:border-[#B49A88]/30 hover:text-[#B49A88]"
        >
          Utforsk markedsplassen <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default SharedProduct;
